import React, { useState } from 'react';
import { Box, Checkbox, FormControlLabel, Popover, Stack } from '@mui/material';
import { ArrowDropDown } from '@mui/icons-material';
import { useTheme } from '@mui/styles';
import TextField from 'c-components/Forms/TextField/TextField';
import { formatPlaceholder, generateTextFieldProps } from 'c-components/Forms/Filterdropdown/funcs';
import type { FilterConfigGroupedSelect, FilterConfigRendererProps } from 'c-pagination/types';
import { useCommonTranslation } from 'c-translation';

const GroupedSelectFilter: React.FC<FilterConfigRendererProps<FilterConfigGroupedSelect>> = ({
    config,
    onChange,
    value,
    id,
    className,
}) => {
    const t = useCommonTranslation();
    const theme = useTheme();
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const selected: (string | number)[] = value ?? [];
    const open = Boolean(anchorEl);
    const popoverId = `${id}-options`;
    const options = config.groups.flatMap(group =>
        group.options.map(option => ({ ...option, label: t(option.label) })),
    );
    const selectedOptions = options.filter(option => selected.includes(option.value));
    const fieldProps = generateTextFieldProps({
        variant: 'outlined',
        size: 'small',
        placeholder: t(config.label),
        theme,
        value: selected,
        options,
        multiple: true,
    });

    const toggleValues = (values: (string | number)[], checked: boolean) => {
        onChange(
            checked
                ? Array.from(new Set([...selected, ...values]))
                : selected.filter(item => !values.includes(item)),
        );
    };

    return (
        <>
            <TextField
                {...fieldProps}
                id={id}
                className={`entity-list-filters ${className}`}
                value=""
                placeholder={formatPlaceholder(selectedOptions, true, t(config.label))}
                InputLabelProps={{ shrink: selectedOptions.length > 0 || open }}
                InputProps={{
                    ...fieldProps?.InputProps,
                    readOnly: true,
                    endAdornment: (
                        <ArrowDropDown
                            sx={{
                                color: 'action.active',
                                transform: open ? 'rotate(180deg)' : undefined,
                            }}
                        />
                    ),
                    inputProps: {
                        role: 'combobox',
                        'aria-label': t(config.label),
                        'aria-haspopup': 'dialog',
                        'aria-expanded': open,
                        'aria-controls': open ? popoverId : undefined,
                        style: { cursor: 'pointer' },
                    },
                }}
                onClick={event => setAnchorEl(event.currentTarget)}
                onKeyDown={event => {
                    if (['Enter', ' ', 'ArrowDown'].includes(event.key)) {
                        event.preventDefault();
                        setAnchorEl(event.currentTarget);
                    }
                }}
            />
            <Popover
                open={open}
                anchorEl={anchorEl}
                onClose={() => setAnchorEl(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            >
                <Box
                    id={popoverId}
                    role="dialog"
                    aria-label={t(config.label)}
                    sx={{ p: 2, minWidth: 240 }}
                >
                    {config.groups.map(group => {
                        const enabledValues = group.options
                            .filter(option => !option.disabled)
                            .map(option => option.value);
                        const selectedCount = enabledValues.filter(item =>
                            selected.includes(item),
                        ).length;
                        const checked =
                            enabledValues.length > 0 && selectedCount === enabledValues.length;
                        const indeterminate = selectedCount > 0 && !checked;

                        return (
                            <Box key={group.label} role="group" aria-label={t(group.label)}>
                                <FormControlLabel
                                    label={t(group.label)}
                                    control={
                                        <Checkbox
                                            checked={checked}
                                            indeterminate={indeterminate}
                                            inputProps={{
                                                'aria-checked': indeterminate ? 'mixed' : checked,
                                            }}
                                            disabled={enabledValues.length === 0}
                                            onChange={(_, nextChecked) =>
                                                toggleValues(enabledValues, nextChecked)
                                            }
                                        />
                                    }
                                />
                                <Stack sx={{ pl: 3 }}>
                                    {group.options.map(option => (
                                        <FormControlLabel
                                            key={option.value}
                                            label={t(option.label)}
                                            disabled={option.disabled}
                                            control={
                                                <Checkbox
                                                    checked={selected.includes(option.value)}
                                                    onChange={(_, nextChecked) =>
                                                        toggleValues([option.value], nextChecked)
                                                    }
                                                />
                                            }
                                        />
                                    ))}
                                </Stack>
                            </Box>
                        );
                    })}
                </Box>
            </Popover>
        </>
    );
};

export default GroupedSelectFilter;
